import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useTenantAthleteProgress,
  useTenantAthleteBilling,
  useTenantAthleteDevelopment,
  useTenantBillingOverview,
  useTenantBootstrap,
  useTenantDevelopmentOverview,
  useTenantDirectory,
  useCreateTenantDrill,
  useCreateTenantLessonPlan,
  useCreateTenantStudentAssignment,
  useCreateTenantStudentGoal,
  useRecordTenantPackageSale,
  useUpdateTenantStudentAssignment,
  useUpdateTenantStudentGoal,
  useUpdateScheduleProgressNote,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@tark/shared/components';
import type {
  TenantAthleteBillingResponse,
  TenantAthleteDevelopmentResponse,
  TenantAthleteProgressResponse,
  TenantDevelopmentOverviewResponse,
  TenantDirectoryAthleteProgressPreview,
  TenantPackagePlan,
} from '@tark/shared/types';
import {
  Activity,
  ChevronRight,
  Search,
  BookOpen,
  Calendar,
  CheckCircle2,
  HeartHandshake,
  ListChecks,
  Loader2,
  MessageCircle,
  PlusCircle,
  ShieldCheck,
  Target,
  UserRound,
  Users,
} from 'lucide-react';
import CoachPageHeader from '../components/CoachPageHeader';
import { CoachFamilyNotesPanel } from '../components/coach-studio/CoachFamilyNotesPanel';

function formatRole(roleCode?: string | null): string {
  if (!roleCode) return 'Member';
  return roleCode
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function roleChipClasses(roleCode?: string | null): string {
  switch ((roleCode || '').toUpperCase()) {
    case 'OWNER':
    case 'ADMIN':
      return 'border-red-500/20 bg-red-500/10 text-red-300';
    case 'COACH':
    case 'STAFF':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'GUARDIAN':
      return 'border-primary/20 bg-primary/10 text-primary';
    default:
      return 'border-border bg-surface-elevated/40 text-text-muted';
  }
}

function formatCodeLabel(value?: string | null, fallback: string = 'None'): string {
  if (!value) return fallback;
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateOnly(value?: string | null): string {
  if (!value) return '';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(amount?: number | null, currency: string = 'USD'): string {
  if (amount == null || Number.isNaN(Number(amount))) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
  }).format(Number(amount));
}

function formatCredits(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '0';
  const rounded = Math.round(Number(value) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function attendanceChipClasses(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'PRESENT':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'LATE':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'ABSENT':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    case 'EXCUSED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    default:
      return 'border-border bg-surface-elevated/40 text-text-subtle';
  }
}

function assignmentChipClasses(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'COMPLETED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'ARCHIVED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-600';
    default:
      return 'border-primary/20 bg-primary/10 text-primary';
  }
}

function goalChipClasses(status?: string | null): string {
  switch ((status || '').toUpperCase()) {
    case 'ACHIEVED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'PAUSED':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'ARCHIVED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-600';
    default:
      return 'border-primary/20 bg-primary/10 text-primary';
  }
}

function timelineLabel(itemType?: string | null): string {
  switch ((itemType || '').toUpperCase()) {
    case 'GOAL_ACHIEVED':
      return 'Goal achieved';
    case 'GOAL_PAUSED':
      return 'Goal paused';
    case 'GOAL_ARCHIVED':
      return 'Goal archived';
    case 'GOAL_CREATED':
      return 'Goal added';
    case 'ASSIGNMENT_COMPLETED':
      return 'Homework done';
    case 'ASSIGNMENT_ASSIGNED':
      return 'Homework assigned';
    default:
      return 'Progress';
  }
}

const selectClassName =
  'w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

type DirectoryKind = 'members' | 'clients' | 'customers' | 'participants';

function resolveDirectoryKind(directoryLabel?: string | null): DirectoryKind {
  switch ((directoryLabel || '').trim().toLowerCase()) {
    case 'clients':
      return 'clients';
    case 'customers':
      return 'customers';
    case 'participants':
      return 'participants';
    default:
      return 'members';
  }
}

function getDirectoryCopy(kind: DirectoryKind, directoryLabel: string, workTabLabel: string) {
  switch (kind) {
    case 'clients':
      return {
        contextDescription: `This page keeps ${directoryLabel.toLowerCase()}, private records, and access in one place while Chats and ${workTabLabel} stay separate.`,
        tertiaryMetricLabel: 'Active Join Codes',
        tertiaryMetricDescription: 'Invite codes currently open for this service workspace.',
        relationshipTitle: 'Client relationship view',
        relationshipEyebrow: 'Service context',
        relationshipDescription: 'Use this space to keep repeat clients, service relationships, and internal handoff details visible without pushing them into the Public Profile.',
        relationshipPoints: [
          `${workTabLabel} stays official for the live service queue and status changes.`,
          'Chats handles pickup timing, clarifications, and repeat-client follow-up.',
          'PROs stays public. Clients stay private.',
        ],
        relationshipEmptyTitle: 'No client relationships yet',
        relationshipEmptyDescription: 'Once the service tools start handling requests, private client relationships will begin to show up here.',
        workspaceTitle: `${directoryLabel} tools`,
        workspaceSummary: 'Use this side for teammate access, invite status, and a live preview of the client-facing records tied to the account.',
        workspaceEmptyTitle: `No ${directoryLabel.toLowerCase()} preview yet`,
        workspaceEmptyDescription: 'Records will appear here once the service tools have active members or linked client relationships.',
        primaryActionLabel: 'Open PROs',
        primaryActionRoute: '/pros',
        accessActionLabel: 'Manage Access',
      };
    case 'customers':
      return {
        contextDescription: `This page keeps ${directoryLabel.toLowerCase()}, private buyer records, and access in one place while Chats and ${workTabLabel} stay separate.`,
        tertiaryMetricLabel: 'Active Join Codes',
        tertiaryMetricDescription: 'Invite codes currently open for this seller workspace.',
        relationshipTitle: 'Customer relationship view',
        relationshipEyebrow: 'Seller context',
        relationshipDescription: 'Use this space to keep buyer relationships, repeat customers, and internal teammate access separate from the public marketplace listing.',
        relationshipPoints: [
          `${workTabLabel} stays official for new orders, buyer requests, and fulfillment handoff.`,
          'Chats handles buyer follow-up and internal coordination around the order flow.',
          'PROs stays public. Customers stay private.',
        ],
        relationshipEmptyTitle: 'No customer relationships yet',
        relationshipEmptyDescription: 'As soon as the private tools start handling requests, customer relationships will begin to surface here.',
        workspaceTitle: `${directoryLabel} tools`,
        workspaceSummary: 'Use this side for private buyer records, teammate access, invite status, and the first preview of customer-linked data.',
        workspaceEmptyTitle: `No ${directoryLabel.toLowerCase()} preview yet`,
        workspaceEmptyDescription: 'Records will appear here once the seller tools have active members or linked customer relationships.',
        primaryActionLabel: 'Open PROs',
        primaryActionRoute: '/pros',
        accessActionLabel: 'Manage Access',
      };
    case 'participants':
      return {
        contextDescription: `This page keeps ${directoryLabel.toLowerCase()}, enrollments, and access in one place while Chats and ${workTabLabel} stay separate.`,
        tertiaryMetricLabel: 'Active Join Codes',
        tertiaryMetricDescription: 'Invite codes currently open for this participant workspace.',
        relationshipTitle: 'Participant view',
        relationshipEyebrow: 'Event context',
        relationshipDescription: 'Use this space to keep enrollment, participant relationships, and internal staffing visible without crowding the live work board.',
        relationshipPoints: [
          `${workTabLabel} stays official for the active event or operations board.`,
          'Chats handles announcements and participant follow-up tied to those events.',
          `${directoryLabel} stays private even when public discovery exists elsewhere.`,
        ],
        relationshipEmptyTitle: 'No participant records yet',
        relationshipEmptyDescription: 'Participant relationships will appear here once the private tools have active enrollment or invite activity.',
        workspaceTitle: `${directoryLabel} tools`,
        workspaceSummary: 'Use this side for participant visibility, invite status, and access.',
        workspaceEmptyTitle: `No ${directoryLabel.toLowerCase()} preview yet`,
        workspaceEmptyDescription: 'Records will appear here once the participant tools have active members or enrollments.',
        primaryActionLabel: 'Open Me',
        primaryActionRoute: '/profile',
        accessActionLabel: 'Manage Access',
      };
    default:
      return {
        contextDescription: 'Private roster, guardians, progress, and access for Coach Studio.',
        tertiaryMetricLabel: 'Students',
        tertiaryMetricDescription: 'Athletes connected to this studio.',
        relationshipTitle: 'Student relationships',
        relationshipEyebrow: 'Student context',
        relationshipDescription: 'Students, guardians, and family context stay here so coaching work does not get buried in the schedule.',
        relationshipPoints: [
          `${workTabLabel} stays official for classes, practices, and sessions.`,
          'Access handles join codes and family invitations.',
          `${directoryLabel} keeps roster, family, and progress in one place.`,
        ],
        relationshipEmptyTitle: 'No students yet',
        relationshipEmptyDescription: 'Create a family join code from Access, then students will appear here when families join.',
        workspaceTitle: 'Access',
        workspaceSummary: 'Join codes, invitations, and staff access stay in Access.',
        workspaceEmptyTitle: 'No students yet',
        workspaceEmptyDescription: 'Use Access to create a family join code or invite the first family.',
        primaryActionLabel: 'Open Family',
        primaryActionRoute: '/family',
        accessActionLabel: 'Open Access',
      };
  }
}

export default function DirectoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentPage, setStudentPage] = useState(0);
  const { activeTenant } = useTenantBootstrap(true);
  const canUseDirectory = Boolean(activeTenant?.tenant?.id);
  const canManageStudentNotes = ['OWNER', 'ADMIN', 'COACH', 'STAFF'].includes(
    (activeTenant?.roleCode || '').toUpperCase()
  );
  const { directory, isLoading, isError, refetch } = useTenantDirectory(canUseDirectory);
  const athletePreview = directory?.access?.athletePreview ?? [];
  const requestedAthleteId = useMemo(() => {
    const value = Number(searchParams.get('athleteId'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [searchParams]);

  useEffect(() => {
    if (requestedAthleteId && athletePreview.some((athlete) => athlete.athleteId === requestedAthleteId)) {
      setSelectedAthleteId(requestedAthleteId);
    }
  }, [athletePreview, requestedAthleteId]);

  const selectedProgressAthleteId = useMemo(() => {
    if (activeTenant?.tenant?.tenantArchetype === 'COACHING') return requestedAthleteId;
    if (selectedAthleteId && athletePreview.some((athlete) => athlete.athleteId === selectedAthleteId)) {
      return selectedAthleteId;
    }
    if (requestedAthleteId && athletePreview.some((athlete) => athlete.athleteId === requestedAthleteId)) {
      return requestedAthleteId;
    }
    return activeTenant?.tenant?.tenantArchetype === 'COACHING' ? null : athletePreview[0]?.athleteId ?? null;
  }, [athletePreview, requestedAthleteId, selectedAthleteId, activeTenant?.tenant?.tenantArchetype]);
  const athleteProgress = useTenantAthleteProgress(
    selectedProgressAthleteId,
    canUseDirectory && selectedProgressAthleteId != null,
  );
  const billingOverview = useTenantBillingOverview(canUseDirectory && canManageStudentNotes);
  const athleteBilling = useTenantAthleteBilling(
    selectedProgressAthleteId,
    canUseDirectory && canManageStudentNotes && selectedProgressAthleteId != null,
  );
  const developmentOverview = useTenantDevelopmentOverview(canUseDirectory && canManageStudentNotes);
  const athleteDevelopment = useTenantAthleteDevelopment(
    selectedProgressAthleteId,
    canUseDirectory && canManageStudentNotes && selectedProgressAthleteId != null,
  );
  const handleSelectAthlete = (athleteId: number) => {
    setSelectedAthleteId(athleteId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('athleteId', String(athleteId));
    setSearchParams(nextParams);
  };

  if (!canUseDirectory) {
    return (
      <div className="space-y-6 pb-24 md:pb-8 max-w-3xl">
        <Card variant="default">
          <CardHeader>
            <CardTitle>Directory appears in tenant mode</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-text-muted">
              Switch into an organization from Me to open the tenant roster and family surface.
            </p>
            <Button onClick={() => navigate('/profile')}>Open Me</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !directory) {
    return (
      <Card variant="default" className="max-w-xl">
        <CardHeader>
          <CardTitle>Directory Unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-muted">
            The tenant directory could not be loaded right now. Try again and we’ll restore your current organization context.
          </p>
          <Button onClick={() => refetch()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const directoryLabel = isCoachStudio ? 'Students' : directory.directoryLabel;
  const workTabLabel = isCoachStudio ? 'Schedule' : directory.workTabLabel;
  const directoryKind = resolveDirectoryKind(directoryLabel);
  const copy = getDirectoryCopy(directoryKind, directoryLabel, workTabLabel);
  const totalVisible = directory.access?.totalDirectoryCount ?? directory.household?.athletes.length ?? 0;
  const activeVisible = directory.access?.activeDirectoryCount ?? 0;
  const linkedAthletes = directory.access?.linkedAthleteCount ?? directory.household?.athletes.length ?? 0;
  const activeJoinCodes = directory.access?.activeJoinCodeCount ?? 0;
  const pendingInvites = directory.access?.pendingInvitationCount ?? 0;
  const relationshipMetricValue = directoryKind === 'members' ? linkedAthletes : activeJoinCodes;
  const hasCoachStudents = !isCoachStudio || linkedAthletes > 0;

  if (isCoachStudio && canManageStudentNotes) {
    const filtered = athletePreview.filter(a => `${a.displayName} ${a.sportCode || ''} ${a.levelCode || ''}`.toLowerCase().includes(studentSearch.toLowerCase()));
    const selected = requestedAthleteId != null;
    return <div className="pro-coach-page space-y-5 pb-24 md:pb-8" id="students">
      <CoachPageHeader title={selected ? athleteProgress.progress?.athlete.displayName || 'Student' : 'Students'}
        subtitle={activeTenant?.tenant?.displayName}
        action={selected ? <Button variant="outline" onClick={() => { setSelectedAthleteId(null); const p=new URLSearchParams(searchParams);p.delete('athleteId');setSearchParams(p); }}>All students</Button> : <Button onClick={() => navigate('/tenant-access#join-codes')}>Add student</Button>} />
      {!selected && !athletePreview.length && <CoachStudentsEmptySetup activeJoinCodes={activeJoinCodes} pendingInvites={pendingInvites} onOpenAccess={() => navigate('/tenant-access#join-codes')} onOpenSchedule={() => navigate('/schedule')} />}
      {selected ? <StudentProgressPanel key={`${activeTenant?.tenant?.id}:${requestedAthleteId}`} detailOnly athletes={athletePreview} selectedAthleteId={selectedProgressAthleteId}
        progress={athleteProgress.progress} billing={athleteBilling.data ?? null} development={athleteDevelopment.data ?? null}
        developmentOverview={developmentOverview.data ?? null} packagePlans={billingOverview.data?.packagePlans ?? []}
        isLoading={athleteProgress.isLoading} isError={athleteProgress.isError}
        isDevelopmentLoading={athleteDevelopment.isLoading || developmentOverview.isLoading}
        isDevelopmentError={athleteDevelopment.isError || developmentOverview.isError} canManageNotes={canManageStudentNotes}
        onSelectAthlete={handleSelectAthlete} onOpenSchedule={() => navigate('/schedule')}
        onRetry={async () => { await Promise.all([athleteProgress.refetch(),athleteBilling.refetch(),billingOverview.refetch(),athleteDevelopment.refetch(),developmentOverview.refetch()]); }} /> : <>
        <label className="pro-search"><Search size={16}/><input aria-label="Find a student" placeholder="Find a student" value={studentSearch} onChange={e=>{setStudentSearch(e.target.value);setStudentPage(0);}} /></label>
        <section className="pro-directory"><div className="pro-student-heading"><span>Student</span><span>Level</span><span>Attendance</span><span/></div>
          {filtered.slice(studentPage*20,studentPage*20+20).map(a=><button className="pro-student-row" key={a.athleteId} onClick={()=>handleSelectAthlete(a.athleteId)}>
            <span className="pro-student-name"><span className="pro-initials">{a.displayName.split(/\s+/).slice(0,2).map(n=>n[0]).join('')}</span><strong>{a.displayName}{(a.unreadFamilyNoteCount || 0)>0 && <small>Family · {a.unreadFamilyNoteCount}</small>}</strong></span>
            <span className="pro-student-level">{formatCodeLabel(a.levelCode,'Student')}</span><span className="pro-student-attendance"><strong>{a.attendedCount} / {a.rosterCount}</strong><small>Attended</small></span><ChevronRight size={16}/>
          </button>)}{!filtered.length && <p className="p-8 text-center text-sm text-text-muted">{athletePreview.length ? 'No matching students.' : 'No students yet.'}</p>}
        </section>
        <div className="flex gap-3">{studentPage>0 && <Button variant="outline" onClick={()=>setStudentPage(p=>p-1)}>Previous</Button>}{(studentPage+1)*20<filtered.length && <Button variant="outline" onClick={()=>setStudentPage(p=>p+1)}>Next</Button>}</div>
      </>}
    </div>;
  }

  return (
    <div id="students" className="w-full space-y-8 pb-24 md:pb-8 scroll-mt-24">
      <CoachPageHeader
        title={isCoachStudio ? 'Students' : directory.activeTenant?.displayName || directoryLabel}
        subtitle={isCoachStudio ? 'Manage students, families, notes, packages, goals, and progress from one roster.' : directory.directoryDescription}
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {isCoachStudio ? directory.activeTenant?.displayName || 'Coach Studio' : `${formatRole(directory.activeRoleCode)} roster`}
          </span>
        }
        action={<>
          {isCoachStudio ? <Button variant="outline" leftIcon={<HeartHandshake className="h-4 w-4" />} onClick={() => navigate('/match-play')}>Match Play</Button> : null}
          {directory.access ? (
            <Button leftIcon={<ShieldCheck className="h-4 w-4" />} onClick={() => navigate('/tenant-access')}>
              {isCoachStudio ? 'Create Join Code' : copy.accessActionLabel}
            </Button>
          ) : null}
        </>}
      />

      {!isCoachStudio ? (
        <Card variant="glass">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Active context</p>
              <p className="mt-2 text-lg font-semibold text-text">
                {directory.activeTenant?.displayName || 'Organization'} • {workTabLabel}
              </p>
              <p className="mt-1 text-sm text-text-muted">{copy.contextDescription}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" leftIcon={<MessageCircle className="h-4 w-4" />} onClick={() => navigate('/squads')}>
                Open Chats
              </Button>
              <Button variant="outline" leftIcon={<Calendar className="h-4 w-4" />} onClick={() => navigate('/schedule')}>
                Open {workTabLabel}
              </Button>
              <Button
                variant="outline"
                leftIcon={<HeartHandshake className="h-4 w-4" />}
                onClick={() => navigate(copy.primaryActionRoute)}
              >
                {copy.primaryActionLabel}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!isCoachStudio || hasCoachStudents ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label={directoryLabel} value={totalVisible} description="Visible records in the current studio." />
          <MetricCard label="Active" value={activeVisible} description="Currently active directory memberships." />
          <MetricCard label={copy.tertiaryMetricLabel} value={relationshipMetricValue} description={copy.tertiaryMetricDescription} />
          <MetricCard label="Pending Invites" value={pendingInvites} description="Outstanding invitations still waiting to be claimed." />
        </div>
      ) : null}

      {isCoachStudio && !hasCoachStudents ? (
        <CoachStudentsEmptySetup
          activeJoinCodes={activeJoinCodes}
          pendingInvites={pendingInvites}
          onOpenAccess={() => navigate('/tenant-access#join-codes')}
          onOpenSchedule={() => navigate('/schedule#upcoming-sessions')}
        />
      ) : null}

      {athletePreview.length > 0 ? (
            <StudentProgressPanel
              athletes={athletePreview}
              selectedAthleteId={selectedProgressAthleteId}
              progress={athleteProgress.progress}
              billing={athleteBilling.data ?? null}
              development={athleteDevelopment.data ?? null}
              developmentOverview={developmentOverview.data ?? null}
              packagePlans={billingOverview.data?.packagePlans ?? []}
              isLoading={athleteProgress.isLoading}
              isError={athleteProgress.isError}
              isDevelopmentLoading={athleteDevelopment.isLoading || developmentOverview.isLoading}
              isDevelopmentError={athleteDevelopment.isError || developmentOverview.isError}
              canManageNotes={canManageStudentNotes}
              onSelectAthlete={handleSelectAthlete}
              onOpenSchedule={() => navigate('/schedule#upcoming-sessions')}
              onRetry={async () => {
                await athleteProgress.refetch();
                await athleteBilling.refetch();
                await billingOverview.refetch();
                await athleteDevelopment.refetch();
                await developmentOverview.refetch();
              }}
            />
      ) : null}

      {hasCoachStudents ? (
      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card variant="default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HeartHandshake className="h-5 w-5 text-primary" />
              {copy.relationshipTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {directoryKind === 'members' ? (
              directory.household ? (
                <>
                  <div className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">{copy.relationshipEyebrow}</p>
                    <p className="mt-2 text-lg font-semibold text-text">
                      {directory.household.guardian?.displayName || 'Guardian household'}
                    </p>
                    <p className="mt-1 text-sm text-text-muted">{directory.household.contextLabel}</p>
                  </div>

                  {directory.household.athletes.length > 0 ? (
                    <div className="space-y-3">
                      {directory.household.athletes.map((athlete) => (
                        <div key={athlete.athleteId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-text">
                                {athlete.preferredName || athlete.givenName} {athlete.familyName}
                              </p>
                              <p className="mt-1 text-sm text-text-muted">
                                {[athlete.sportCode, athlete.levelCode, athlete.relationshipCode].filter(Boolean).join(' • ') || 'Enrolled athlete'}
                              </p>
                            </div>
                            <div className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
                              Athlete
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyStateCard
                      title={directory.household.emptyStateTitle || 'No linked athletes yet'}
                      description={directory.household.emptyStateDescription || 'Add your first athlete to get started.'}
                    />
                  )}
                </>
              ) : (
                <EmptyStateCard
                  title={copy.relationshipEmptyTitle}
                  description={copy.relationshipEmptyDescription}
                />
              )
            ) : (
              <>
                <div className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-subtle">{copy.relationshipEyebrow}</p>
                  <p className="mt-2 text-lg font-semibold text-text">
                    {directory.activeTenant?.displayName || directoryLabel}
                  </p>
                  <p className="mt-1 text-sm text-text-muted">{copy.relationshipDescription}</p>
                </div>

                <div className="space-y-3">
                  {copy.relationshipPoints.map((point) => (
                    <div key={point} className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
                      <p className="text-sm leading-6 text-text-muted">{point}</p>
                    </div>
                  ))}
                </div>

                {directory.access?.memberPreview.length ? (
                  <div className="space-y-3">
                    {directory.access.memberPreview.slice(0, 3).map((member) => (
                      <DirectoryMemberPreview key={member.membershipId} member={member} />
                    ))}
                  </div>
                ) : (
                  <EmptyStateCard
                    title={copy.relationshipEmptyTitle}
                    description={copy.relationshipEmptyDescription}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card variant="default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {copy.workspaceTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {directory.access ? (
              <>
                <div className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
                  <p className="font-semibold text-text">
                    {directory.access.totalDirectoryCount} total • {directory.access.activeJoinCodeCount} active join codes
                  </p>
                  <p className="mt-2 text-sm text-text-muted">
                    {copy.workspaceSummary}
                  </p>
                </div>

                {directory.access.memberPreview.length > 0 ? (
                  <div className="space-y-3">
                    {directory.access.memberPreview.map((member) => (
                      <DirectoryMemberPreview key={member.membershipId} member={member} />
                    ))}
                  </div>
                ) : (
                  <EmptyStateCard
                    title={copy.workspaceEmptyTitle}
                    description={copy.workspaceEmptyDescription}
                  />
                )}

                <Button onClick={() => navigate('/tenant-access')}>
                  {copy.accessActionLabel}
                </Button>
              </>
            ) : (
              <EmptyStateCard
                title="Manager tools unavailable"
                description={
                  directoryKind === 'members'
                    ? 'Your current role can still use the household side of the directory, but live membership management stays available only to coaches and admins.'
                    : `Your current role can still use the ${directoryLabel.toLowerCase()} side of the directory, but deeper access remains limited.`
                }
                icon={<ShieldCheck className="h-4 w-4 text-primary" />}
              />
            )}
          </CardContent>
        </Card>
      </div>
      ) : null}
    </div>
  );
}

function CoachStudentsEmptySetup({
  activeJoinCodes,
  pendingInvites,
  onOpenAccess,
  onOpenSchedule,
}: {
  activeJoinCodes: number;
  pendingInvites: number;
  onOpenAccess: () => void;
  onOpenSchedule: () => void;
}) {
  return (
    <Card variant="default" className="border-primary/15">
      <CardContent className="grid gap-5 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">First students</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-text">Invite the first family</h2>
          <p className="mt-2 max-w-2xl text-sm font-medium text-text-muted">
            Create a family join code. When guardians join and add athletes, students appear here.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <MiniSetupMetric label="Join Codes" value={activeJoinCodes} />
            <MiniSetupMetric label="Invites" value={pendingInvites} />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <Button onClick={onOpenAccess} leftIcon={<ShieldCheck className="h-4 w-4" />}>
            Create Join Code
          </Button>
          <Button variant="outline" onClick={onOpenSchedule} leftIcon={<Calendar className="h-4 w-4" />}>
            Open Schedule
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniSetupMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-surface-elevated/20 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold text-text">{value}</p>
    </div>
  );
}

function StudentProgressPanel({
  detailOnly = false,
  athletes,
  selectedAthleteId,
  progress,
  billing,
  development,
  developmentOverview,
  packagePlans,
  isLoading,
  isError,
  isDevelopmentLoading,
  isDevelopmentError,
  canManageNotes,
  onSelectAthlete,
  onOpenSchedule,
  onRetry,
}: {
  detailOnly?: boolean;
  athletes: TenantDirectoryAthleteProgressPreview[];
  selectedAthleteId: number | null;
  progress: TenantAthleteProgressResponse | null;
  billing: TenantAthleteBillingResponse | null;
  development: TenantAthleteDevelopmentResponse | null;
  developmentOverview: TenantDevelopmentOverviewResponse | null;
  packagePlans: TenantPackagePlan[];
  isLoading: boolean;
  isError: boolean;
  isDevelopmentLoading: boolean;
  isDevelopmentError: boolean;
  canManageNotes: boolean;
  onSelectAthlete: (athleteId: number) => void;
  onOpenSchedule: () => void;
  onRetry: () => void | Promise<unknown>;
}) {
  const updateProgressNote = useUpdateScheduleProgressNote();
  const recordPackageSale = useRecordTenantPackageSale();
  const [editingBookingId, setEditingBookingId] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [salePlanId, setSalePlanId] = useState('');
  const [saleCredits, setSaleCredits] = useState('5');
  const [saleAmount, setSaleAmount] = useState('');
  const [saleMethod, setSaleMethod] = useState('CASH');
  const [saleError, setSaleError] = useState<string | null>(null);
  const [studentDetailTab, setStudentDetailTab] = useState<'attendance' | 'plans' | 'goals'>('attendance');
  const progressGuardians = progress?.guardians ?? [];
  const hasProgressStats = Boolean(
    progress &&
      (progress.stats.rosterCount > 0 ||
        progress.stats.attendedCount > 0 ||
        progress.stats.absentCount > 0 ||
        progress.stats.excusedCount > 0 ||
        progress.stats.upcomingCount > 0)
  );
  const hasPackageSignals = Boolean(
    billing &&
      ((billing.athlete.creditsRemaining || 0) > 0 ||
        (billing.athlete.activePackageCount || 0) > 0 ||
        (billing.athlete.unpaidBookingCount || 0) > 0 ||
        (billing.athlete.lowCreditPackageCount || 0) > 0 ||
        (billing.packages?.length ?? 0) > 0)
  );
  const selectedPlan = packagePlans.find((plan) => String(plan.packagePlanId) === salePlanId);

  useEffect(() => {
    setStudentDetailTab('attendance');
  }, [selectedAthleteId]);

  const openSessionNoteEditor = (session: TenantAthleteProgressResponse['recentSessions'][number]) => {
    setEditingBookingId(session.bookingId);
    setNoteDraft(session.progressSummary || '');
    setNoteError(null);
  };

  const saveSessionNote = async (session: TenantAthleteProgressResponse['recentSessions'][number]) => {
    if (!session.attendanceStatus) {
      setNoteError('Mark attendance before adding a coach note.');
      return;
    }

    setNoteError(null);
    try {
      await updateProgressNote.mutateAsync({
        occurrenceId: session.occurrenceId,
        bookingId: session.bookingId,
        payload: { progressSummary: noteDraft.trim() || null },
      });
      setEditingBookingId(null);
      setNoteDraft('');
      await Promise.resolve(onRetry());
    } catch {
      setNoteError('Could not save this note right now.');
    }
  };

  const submitPackageSale = async () => {
    if (!selectedAthleteId) {
      setSaleError('Choose a student first.');
      return;
    }

    const credits = selectedPlan ? selectedPlan.sessionCreditCount : Number(saleCredits);
    const amount = selectedPlan && saleAmount.trim() === '' ? selectedPlan.priceAmount ?? 0 : Number(saleAmount || 0);
    if (!Number.isFinite(credits) || credits <= 0) {
      setSaleError('Enter valid credits.');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      setSaleError('Enter a valid amount.');
      return;
    }

    setSaleError(null);
    try {
      await recordPackageSale.mutateAsync({
        athleteId: selectedAthleteId,
        payload: {
          packagePlanId: selectedPlan?.packagePlanId ?? null,
          packageLabel: selectedPlan ? undefined : `${formatCredits(credits)} lesson package`,
          packageType: selectedPlan?.packageType || 'PRIVATE_LESSON',
          sessionCreditCount: selectedPlan ? undefined : credits,
          purchaseAmount: amount,
          currencyCode: selectedPlan?.currencyCode || 'USD',
          paymentStatus: amount === 0 ? 'WAIVED' : 'PAID',
          manualPaymentMethod: saleMethod,
          note: 'Recorded from student profile',
        },
      });
      setSaleCredits('5');
      setSaleAmount('');
      await Promise.resolve(onRetry());
    } catch {
      setSaleError('Could not record this package. Do not enter card or bank details.');
    }
  };

  return (
    <Card variant="default" padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Student progress</CardTitle>
        </div>
        <span className="rounded-full border border-border bg-surface-elevated/40 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">
          {athletes.length} active
        </span>
      </div>

      <div className={detailOnly ? "min-w-0" : "grid gap-0 xl:grid-cols-[0.88fr_1.12fr]"}>
        {!detailOnly && <div className="border-b border-border p-4 xl:border-b-0 xl:border-r">
          <div className="space-y-2">
            {athletes.map((athlete) => {
              const selected = athlete.athleteId === selectedAthleteId;
              const hasAthleteProgress =
                athlete.attendedCount > 0 ||
                athlete.missedCount > 0 ||
                athlete.notesCount > 0 ||
                athlete.upcomingCount > 0;
              return (
                <button
                  key={athlete.athleteId}
                  type="button"
                  onClick={() => onSelectAthlete(athlete.athleteId)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                    selected
                      ? 'border-primary/40 bg-primary/10'
                      : 'border-border bg-surface-elevated/20 hover:border-primary/30 hover:bg-surface-elevated'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">{athlete.displayName}</p>
                      <p className="mt-1 text-xs text-text-muted">
                        {[formatCodeLabel(athlete.sportCode, ''), formatCodeLabel(athlete.levelCode, '')]
                          .filter(Boolean)
                          .join(' • ') || 'Student'}
                      </p>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${attendanceChipClasses(athlete.latestAttendanceStatus)}`}>
                      {formatCodeLabel(athlete.latestAttendanceStatus, 'New')}
                    </span>
                    {(athlete.unreadFamilyNoteCount ?? 0) > 0 ? (
                      <span className="rounded-full bg-primary px-2 py-1 text-[10px] font-semibold text-white">
                        Family {athlete.unreadFamilyNoteCount! > 99 ? '99+' : athlete.unreadFamilyNoteCount}
                      </span>
                    ) : null}
                  </div>
                  {hasAthleteProgress ? (
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                      <MiniProgressMetric label="Attend" value={athlete.attendedCount} />
                      <MiniProgressMetric label="Miss" value={athlete.missedCount} />
                      <MiniProgressMetric label="Notes" value={athlete.notesCount} />
                      <MiniProgressMetric label="Next" value={athlete.upcomingCount} />
                    </div>
                  ) : null}
                  {athlete.latestProgressSummary ? (
                    <p className="mt-3 line-clamp-2 text-xs leading-5 text-text-muted">
                      {athlete.latestProgressSummary}
                    </p>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>}

        <div className="min-w-0 p-4">
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800">Student history unavailable</p>
              <Button variant="outline" className="mt-3" onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : progress ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-text">{progress.athlete.displayName}</p>
                  <p className="mt-1 text-sm text-text-muted">
                    {[formatCodeLabel(progress.athlete.sportCode, ''), formatCodeLabel(progress.athlete.levelCode, '')]
                      .filter(Boolean)
                      .join(' • ') || 'Student'}
                  </p>
                </div>
                <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                  {progress.stats.notesCount} notes
                </span>
              </div>

              {hasProgressStats ? (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <ProgressStat label="Roster" value={progress.stats.rosterCount} />
                  <ProgressStat label="Attend" value={progress.stats.attendedCount} />
                  <ProgressStat label="Missed" value={progress.stats.absentCount + progress.stats.excusedCount} />
                  <ProgressStat label="Next" value={progress.stats.upcomingCount} />
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-4">
                  <p className="text-sm font-semibold text-text">No session history yet</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={onOpenSchedule}>
                    Create Session
                  </Button>
                </div>
              )}

              <CoachFamilyNotesPanel progress={progress} />

              <StudentDetailTabs
                activeTab={studentDetailTab}
                canUseDevelopment={canManageNotes}
                onChange={setStudentDetailTab}
              />

              {canManageNotes && studentDetailTab !== 'attendance' ? (
                <StudentDevelopmentPanel
                  mode={studentDetailTab}
                  athleteId={selectedAthleteId}
                  development={development}
                  overview={developmentOverview}
                  isLoading={isDevelopmentLoading}
                  isError={isDevelopmentError}
                  onRetry={onRetry}
                />
              ) : null}

              {studentDetailTab === 'attendance' ? (
                <>
              {canManageNotes ? (
                <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-text">Packages</p>
                      <p className="mt-1 text-xs text-text-muted">Manual/off-platform only. No card or bank details.</p>
                    </div>
                    <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                      {formatCredits(billing?.athlete.creditsRemaining)} credits
                    </span>
                  </div>

                  {hasPackageSignals ? (
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <MiniProgressMetric label="Plans" value={billing?.athlete.activePackageCount ?? 0} />
                      <MiniProgressMetric label="Unpaid" value={billing?.athlete.unpaidBookingCount ?? 0} />
                      <MiniProgressMetric label="Low" value={billing?.athlete.lowCreditPackageCount ?? 0} />
                    </div>
                  ) : null}

                  {billing?.packages?.length ? (
                    <div className="mt-3 space-y-2">
                      {billing.packages.slice(0, 3).map((pkg) => (
                        <div key={pkg.athletePackageId} className="rounded-xl border border-border bg-surface px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-text">{pkg.packageLabel}</p>
                            <span className="text-xs font-semibold text-primary">
                              {formatCredits(pkg.creditsRemaining)}/{formatCredits(pkg.creditsTotal)}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-text-muted">
                            {formatMoney(pkg.purchaseAmount, pkg.currencyCode)} • {formatCodeLabel(pkg.paymentStatus)}
                            {pkg.expiresAt ? ` • expires ${formatDateTime(pkg.expiresAt)}` : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-4 rounded-xl border border-border bg-surface p-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-text">Package</label>
                        <select
                          className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          value={salePlanId}
                          onChange={(event) => {
                            const nextPlanId = event.target.value;
                            setSalePlanId(nextPlanId);
                            const nextPlan = packagePlans.find((plan) => String(plan.packagePlanId) === nextPlanId);
                            setSaleAmount(nextPlan?.priceAmount != null ? String(nextPlan.priceAmount) : '');
                          }}
                        >
                          <option value="">Custom package</option>
                          {packagePlans.filter((plan) => plan.active).map((plan) => (
                            <option key={plan.packagePlanId} value={plan.packagePlanId}>
                              {plan.planName} ({formatCredits(plan.sessionCreditCount)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <Input
                        label="Credits"
                        type="number"
                        min="0"
                        step="0.5"
                        value={selectedPlan ? String(selectedPlan.sessionCreditCount) : saleCredits}
                        disabled={Boolean(selectedPlan)}
                        onChange={(event) => setSaleCredits(event.target.value)}
                      />
                      <Input
                        label="Amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={saleAmount}
                        onChange={(event) => setSaleAmount(event.target.value)}
                      />
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-text">Method label</label>
                        <select
                          className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          value={saleMethod}
                          onChange={(event) => setSaleMethod(event.target.value)}
                        >
                      {['CASH', 'CHECK', 'ZELLE', 'VENMO', 'CASH_APP', 'OTHER_OFF_PLATFORM', 'COMP'].map((method) => (
                            <option key={method} value={method}>
                              {formatCodeLabel(method)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {saleError ? <p className="mt-2 text-sm text-red-500">{saleError}</p> : null}
                    <div className="mt-3 flex justify-end">
                      <Button size="sm" isLoading={recordPackageSale.isPending} onClick={submitPackageSale}>
                        Record package
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}

              {progressGuardians.length > 0 ? (
                <div className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-text">Family</p>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
                      {progressGuardians.length} guardian{progressGuardians.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-2 lg:grid-cols-2">
                    {progressGuardians.map((guardian) => (
                      <div key={guardian.guardianProfileId} className="rounded-xl border border-border bg-surface px-3 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-text">{guardian.displayName}</p>
                          {guardian.primaryGuardian ? (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                              Primary
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-text-muted">
                          {formatCodeLabel(guardian.relationshipCode, 'Guardian')}
                        </p>
                        {[guardian.email, guardian.mobileNumber, guardian.emergencyContactPhone]
                          .filter((item): item is string => Boolean(item))
                          .slice(0, 2)
                          .map((item) => (
                            <p key={`${guardian.guardianProfileId}-${item}`} className="mt-1 truncate text-xs text-text-secondary">
                              {item}
                            </p>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {progress.recentSessions.length > 0 ? (
                <div className="space-y-2">
                  {progress.recentSessions.map((session) => (
                    <div key={session.bookingId} className="rounded-2xl border border-border bg-surface-elevated/20 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-text">{session.title}</p>
                          <p className="mt-1 text-xs text-text-muted">
                            {[formatDateTime(session.startsAt), session.programName, session.locationLabel]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${attendanceChipClasses(session.attendanceStatus)}`}>
                          {formatCodeLabel(session.attendanceStatus, 'Not marked')}
                        </span>
                      </div>
                      {session.progressSummary && editingBookingId !== session.bookingId ? (
                        <div className="mt-3 rounded-xl border border-primary/15 bg-primary/5 px-3 py-2">
                          <p className="text-sm leading-6 text-text">{session.progressSummary}</p>
                        </div>
                      ) : null}
                      {canManageNotes && session.attendanceStatus ? (
                        editingBookingId === session.bookingId ? (
                          <div className="mt-3 rounded-xl border border-primary/20 bg-surface p-3">
                            <Textarea
                              label="Coach note"
                              value={noteDraft}
                              maxLength={1000}
                              rows={3}
                              hint="What should this coach remember for next time?"
                              onChange={(event) => setNoteDraft(event.target.value)}
                            />
                            {noteError ? <p className="mt-2 text-sm text-red-500">{noteError}</p> : null}
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-xs text-text-subtle">{noteDraft.length}/1000</p>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingBookingId(null);
                                    setNoteDraft('');
                                    setNoteError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  isLoading={updateProgressNote.isPending}
                                  onClick={() => saveSessionNote(session)}
                                >
                                  Save note
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3">
                            <Button size="sm" variant="ghost" onClick={() => openSessionNoteEditor(session)}>
                              {session.progressSummary ? 'Edit note' : 'Add note'}
                            </Button>
                          </div>
                        )
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyStateCard
                  title="No session history yet"
                  description="Attendance and notes appear here after this student is added to a session roster."
                  icon={<Activity className="h-4 w-4 text-primary" />}
                />
              )}
                </>
              ) : null}
            </div>
          ) : (
            <EmptyStateCard
              title="Pick a student"
              description="Select a student to view attendance and recent coach notes."
              icon={<BookOpen className="h-4 w-4 text-primary" />}
            />
          )}
        </div>
      </div>
    </Card>
  );
}

function StudentDetailTabs({
  activeTab,
  canUseDevelopment,
  onChange,
}: {
  activeTab: 'attendance' | 'plans' | 'goals';
  canUseDevelopment: boolean;
  onChange: (tab: 'attendance' | 'plans' | 'goals') => void;
}) {
  const tabs: Array<{ key: 'attendance' | 'plans' | 'goals'; label: string; disabled?: boolean }> = [
    { key: 'attendance', label: 'Attendance' },
    { key: 'plans', label: 'Plans', disabled: !canUseDevelopment },
    { key: 'goals', label: 'Goals', disabled: !canUseDevelopment },
  ];

  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 p-1">
      <div className="grid grid-cols-3 gap-1">
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              disabled={tab.disabled}
              onClick={() => onChange(tab.key)}
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                active
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-text-muted hover:bg-white/70 hover:text-text'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StudentDevelopmentPanel({
  mode,
  athleteId,
  development,
  overview,
  isLoading,
  isError,
  onRetry,
}: {
  mode: 'plans' | 'goals';
  athleteId: number | null;
  development: TenantAthleteDevelopmentResponse | null;
  overview: TenantDevelopmentOverviewResponse | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void | Promise<unknown>;
}) {
  const createDrill = useCreateTenantDrill();
  const createLessonPlan = useCreateTenantLessonPlan();
  const createAssignment = useCreateTenantStudentAssignment();
  const updateAssignment = useUpdateTenantStudentAssignment();
  const createGoal = useCreateTenantStudentGoal();
  const updateGoal = useUpdateTenantStudentGoal();
  const drills = development?.drills ?? overview?.drills?.filter((drill) => drill.active) ?? [];
  const lessonPlans =
    development?.lessonPlans ?? overview?.lessonPlans?.filter((plan) => plan.planStatus === 'ACTIVE') ?? [];
  const assignments = development?.assignments ?? [];
  const goals = development?.goals ?? [];
  const timeline = development?.timeline ?? [];
  const summary = development?.summary ?? overview?.summary ?? {
    activeDrillCount: drills.length,
    activeLessonPlanCount: lessonPlans.length,
    openAssignmentCount: assignments.filter((item) => item.assignmentStatus === 'ASSIGNED').length,
    completedAssignmentCount: assignments.filter((item) => item.assignmentStatus === 'COMPLETED').length,
    activeGoalCount: goals.filter((item) => item.goalStatus === 'ACTIVE').length,
    achievedGoalCount: goals.filter((item) => item.goalStatus === 'ACHIEVED').length,
  };
  const activeGoalCount = summary.activeGoalCount ?? goals.filter((item) => item.goalStatus === 'ACTIVE').length;
  const achievedGoalCount = summary.achievedGoalCount ?? goals.filter((item) => item.goalStatus === 'ACHIEVED').length;
  const hasDevelopmentSummary =
    summary.openAssignmentCount > 0 ||
    summary.completedAssignmentCount > 0 ||
    activeGoalCount > 0 ||
    summary.activeLessonPlanCount > 0;

  const [assignmentKind, setAssignmentKind] = useState<'DRILL' | 'PLAN'>('DRILL');
  const [assignmentTargetId, setAssignmentTargetId] = useState('');
  const [assignmentDueDate, setAssignmentDueDate] = useState('');
  const [assignmentNote, setAssignmentNote] = useState('');
  const [assignmentShare, setAssignmentShare] = useState(true);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillFocus, setDrillFocus] = useState('');
  const [drillDuration, setDrillDuration] = useState('');
  const [drillError, setDrillError] = useState<string | null>(null);
  const [planTitle, setPlanTitle] = useState('');
  const [planDrillId, setPlanDrillId] = useState('');
  const [planError, setPlanError] = useState<string | null>(null);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalFocus, setGoalFocus] = useState('');
  const [goalTargetDate, setGoalTargetDate] = useState('');
  const [goalNotes, setGoalNotes] = useState('');
  const [goalShare, setGoalShare] = useState(true);
  const [goalError, setGoalError] = useState<string | null>(null);

  useEffect(() => {
    setAssignmentTargetId('');
    setAssignmentError(null);
  }, [assignmentKind, athleteId]);

  const assignmentOptions =
    assignmentKind === 'DRILL'
      ? drills.map((drill) => ({ id: drill.drillId, title: drill.title }))
      : lessonPlans.map((plan) => ({ id: plan.lessonPlanId, title: plan.title }));
  const selectedAssignmentTarget = assignmentOptions.find((item) => String(item.id) === assignmentTargetId);

  const submitAssignment = async () => {
    if (!athleteId) {
      setAssignmentError('Pick a student first.');
      return;
    }
    if (!selectedAssignmentTarget) {
      setAssignmentError(`Choose a ${assignmentKind === 'DRILL' ? 'drill' : 'lesson plan'}.`);
      return;
    }

    setAssignmentError(null);
    try {
      await createAssignment.mutateAsync({
        athleteId,
        drillId: assignmentKind === 'DRILL' ? Number(assignmentTargetId) : null,
        lessonPlanId: assignmentKind === 'PLAN' ? Number(assignmentTargetId) : null,
        title: selectedAssignmentTarget.title,
        description: assignmentNote.trim() || null,
        dueAt: assignmentDueDate ? new Date(`${assignmentDueDate}T12:00:00`).toISOString() : null,
        shareWithFamily: assignmentShare,
      });
      setAssignmentTargetId('');
      setAssignmentDueDate('');
      setAssignmentNote('');
      setAssignmentShare(true);
      await Promise.resolve(onRetry());
    } catch {
      setAssignmentError('Could not assign this homework right now.');
    }
  };

  const submitGoal = async () => {
    if (!athleteId) {
      setGoalError('Pick a student first.');
      return;
    }
    if (!goalTitle.trim()) {
      setGoalError('Add a goal title.');
      return;
    }

    setGoalError(null);
    try {
      await createGoal.mutateAsync({
        athleteId,
        title: goalTitle.trim(),
        focusArea: goalFocus.trim() || null,
        notes: goalNotes.trim() || null,
        goalStatus: 'ACTIVE',
        targetDate: goalTargetDate || null,
        shareWithFamily: goalShare,
      });
      setGoalTitle('');
      setGoalFocus('');
      setGoalTargetDate('');
      setGoalNotes('');
      setGoalShare(true);
      await Promise.resolve(onRetry());
    } catch {
      setGoalError('Could not save this goal.');
    }
  };

  const updateGoalStatus = async (
    goal: NonNullable<TenantAthleteDevelopmentResponse['goals']>[number],
    nextStatus: string,
  ) => {
    try {
      await updateGoal.mutateAsync({
        goalId: goal.goalId,
        athleteId: goal.athleteId,
        payload: {
          title: goal.title,
          focusArea: goal.focusArea ?? null,
          notes: goal.notes ?? null,
          goalStatus: nextStatus,
          targetDate: goal.targetDate ?? null,
          shareWithFamily: goal.shareWithFamily,
        },
      });
      await Promise.resolve(onRetry());
    } catch {
      setGoalError('Could not update this goal.');
    }
  };

  const submitDrill = async () => {
    const duration = drillDuration.trim() ? Number(drillDuration) : null;
    if (!drillTitle.trim()) {
      setDrillError('Add a drill title.');
      return;
    }
    if (duration != null && (!Number.isFinite(duration) || duration <= 0)) {
      setDrillError('Duration must be a valid number.');
      return;
    }

    setDrillError(null);
    try {
      const drill = await createDrill.mutateAsync({
        title: drillTitle.trim(),
        skillFocus: drillFocus.trim() || null,
        durationMinutes: duration,
        active: true,
      });
      setDrillTitle('');
      setDrillFocus('');
      setDrillDuration('');
      setAssignmentKind('DRILL');
      setAssignmentTargetId(String(drill.drillId));
      await Promise.resolve(onRetry());
    } catch {
      setDrillError('Could not save this drill.');
    }
  };

  const submitLessonPlan = async () => {
    if (!planTitle.trim()) {
      setPlanError('Add a plan title.');
      return;
    }
    const drill = drills.find((item) => String(item.drillId) === planDrillId);

    setPlanError(null);
    try {
      const plan = await createLessonPlan.mutateAsync({
        title: planTitle.trim(),
        planStatus: 'ACTIVE',
        items: [
          {
            drillId: drill?.drillId ?? null,
            title: drill?.title ?? planTitle.trim(),
            durationMinutes: drill?.durationMinutes ?? null,
            itemOrder: 1,
          },
        ],
      });
      setPlanTitle('');
      setPlanDrillId('');
      setAssignmentKind('PLAN');
      setAssignmentTargetId(String(plan.lessonPlanId));
      await Promise.resolve(onRetry());
    } catch {
      setPlanError('Could not save this lesson plan.');
    }
  };

  const updateAssignmentStatus = async (
    assignment: NonNullable<TenantAthleteDevelopmentResponse['assignments']>[number],
    nextStatus: string,
  ) => {
    try {
      await updateAssignment.mutateAsync({
        assignmentId: assignment.assignmentId,
        athleteId: assignment.athleteId,
        payload: {
          assignmentStatus: nextStatus,
          description: assignment.description ?? null,
          dueAt: assignment.dueAt ?? null,
          shareWithFamily: assignment.shareWithFamily,
        },
      });
      await Promise.resolve(onRetry());
    } catch {
      setAssignmentError('Could not update this assignment.');
    }
  };

  return (
    <div id={mode === 'goals' ? 'goals' : 'plans'} className="scroll-mt-24 rounded-2xl border border-border bg-surface-elevated/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-text">{mode === 'goals' ? 'Goals' : 'Plans'}</p>
        </div>
        {isError ? (
          <Button size="sm" variant="outline" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          Loading development
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {hasDevelopmentSummary ? (
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
              <MiniProgressMetric label="Open" value={summary.openAssignmentCount} />
              <MiniProgressMetric label="Done" value={summary.completedAssignmentCount} />
              <MiniProgressMetric label="Goals" value={activeGoalCount} />
              <MiniProgressMetric label="Plans" value={summary.activeLessonPlanCount} />
            </div>
          ) : null}

          <div className="space-y-3">
            {mode === 'goals' ? (
            <div id="goals" className="scroll-mt-24 rounded-xl border border-border bg-surface p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold text-text">Goals</p>
                </div>
                <span className="rounded-full border border-border bg-surface-elevated/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                  {achievedGoalCount} achieved
                </span>
              </div>

              <div className="grid gap-3 md:grid-cols-[1fr_0.72fr]">
                <Input
                  label="Goal"
                  value={goalTitle}
                  hint="Short outcome for this student."
                  onChange={(event) => setGoalTitle(event.target.value)}
                />
                <Input
                  label="Target"
                  type="date"
                  value={goalTargetDate}
                  onChange={(event) => setGoalTargetDate(event.target.value)}
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[0.72fr_1fr]">
                <Input
                  label="Focus"
                  value={goalFocus}
                  hint="Skill area or theme."
                  onChange={(event) => setGoalFocus(event.target.value)}
                />
                <Textarea
                  label="Note"
                  rows={2}
                  maxLength={500}
                  value={goalNotes}
                  hint="What does progress look like?"
                  onChange={(event) => setGoalNotes(event.target.value)}
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-text-muted">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary"
                    checked={goalShare}
                    onChange={(event) => setGoalShare(event.target.checked)}
                  />
                  Share with family
                </label>
                <Button size="sm" variant="outline" isLoading={createGoal.isPending} onClick={submitGoal}>
                  Add goal
                </Button>
              </div>
              {goalError ? <p className="mt-2 text-sm text-red-500">{goalError}</p> : null}

              {goals.length > 0 ? (
                <div className="mt-4 space-y-2">
                  {goals.slice(0, 4).map((goal) => (
                    <div key={goal.goalId} className="rounded-xl border border-border bg-surface-elevated/20 px-3 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-text">{goal.title}</p>
                          <p className="mt-1 text-xs text-text-muted">
                            {[goal.focusArea, goal.targetDate ? `target ${formatDateOnly(goal.targetDate)}` : null]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${goalChipClasses(goal.goalStatus)}`}>
                          {formatCodeLabel(goal.goalStatus, 'Active')}
                        </span>
                      </div>
                      {goal.notes ? <p className="mt-2 line-clamp-2 text-xs leading-5 text-text-muted">{goal.notes}</p> : null}
                      {goal.goalStatus !== 'ARCHIVED' && goal.goalStatus !== 'ACHIEVED' ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                            isLoading={updateGoal.isPending}
                            onClick={() => updateGoalStatus(goal, 'ACHIEVED')}
                          >
                            Achieved
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isLoading={updateGoal.isPending}
                            onClick={() => updateGoalStatus(goal, goal.goalStatus === 'PAUSED' ? 'ACTIVE' : 'PAUSED')}
                          >
                            {goal.goalStatus === 'PAUSED' ? 'Resume' : 'Pause'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isLoading={updateGoal.isPending}
                            onClick={() => updateGoalStatus(goal, 'ARCHIVED')}
                          >
                            Archive
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border bg-surface-elevated/20 p-4">
                  <p className="text-sm font-semibold text-text">No goals yet</p>
                </div>
              )}
            </div>
            ) : null}

            {mode === 'plans' ? (
            <div id="plans" className="scroll-mt-24 rounded-xl border border-border bg-surface p-3">
              <div className="mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <p className="text-sm font-semibold text-text">Progress timeline</p>
              </div>
              {timeline.length > 0 ? (
                <div className="space-y-2">
                  {timeline.slice(0, 8).map((item) => (
                    <div key={item.itemId} className="flex gap-3 rounded-xl border border-border bg-surface-elevated/20 px-3 py-3">
                      <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
                            {timelineLabel(item.itemType)}
                          </p>
                          <span className="text-[10px] text-text-subtle">
                            {formatDateTime(item.occurredAt)}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm font-semibold text-text">{item.title}</p>
                        {item.description ? (
                          <p className="mt-1 line-clamp-2 text-xs leading-5 text-text-muted">{item.description}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-surface-elevated/20 p-4">
                  <p className="text-sm font-semibold text-text">No progress yet</p>
                </div>
              )}
            </div>
            ) : null}
          </div>

          {mode === 'plans' ? (
          <>
          <div className="rounded-xl border border-border bg-surface p-3">
            <div className="grid gap-3 md:grid-cols-[0.7fr_1.3fr_0.8fr]">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text">Type</label>
                <select
                  className={selectClassName}
                  value={assignmentKind}
                  onChange={(event) => setAssignmentKind(event.target.value as 'DRILL' | 'PLAN')}
                >
                  <option value="DRILL">Drill</option>
                  <option value="PLAN">Lesson plan</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text">Assign</label>
                <select
                  className={selectClassName}
                  value={assignmentTargetId}
                  onChange={(event) => setAssignmentTargetId(event.target.value)}
                >
                  <option value="">Choose...</option>
                  {assignmentOptions.map((item) => (
                    <option key={`${assignmentKind}-${item.id}`} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Due"
                type="date"
                value={assignmentDueDate}
                onChange={(event) => setAssignmentDueDate(event.target.value)}
              />
            </div>
            <Textarea
              className="mt-3"
              label="Note"
              rows={2}
              maxLength={500}
              value={assignmentNote}
              hint="Optional focus for the student."
              onChange={(event) => setAssignmentNote(event.target.value)}
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-text-muted">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border text-primary"
                  checked={assignmentShare}
                  onChange={(event) => setAssignmentShare(event.target.checked)}
                />
                Share with family
              </label>
              <Button size="sm" isLoading={createAssignment.isPending} onClick={submitAssignment}>
                Assign homework
              </Button>
            </div>
            {assignmentError ? <p className="mt-2 text-sm text-red-500">{assignmentError}</p> : null}
          </div>

          {assignments.length > 0 ? (
            <div className="space-y-2">
              {assignments.slice(0, 4).map((assignment) => (
                <div key={assignment.assignmentId} className="rounded-xl border border-border bg-surface px-3 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">{assignment.title}</p>
                      <p className="mt-1 text-xs text-text-muted">
                        {[assignment.lessonPlanTitle ? 'Plan' : assignment.drillTitle ? 'Drill' : 'Homework', assignment.dueAt ? `due ${formatDateTime(assignment.dueAt)}` : null]
                          .filter(Boolean)
                          .join(' • ')}
                      </p>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${assignmentChipClasses(assignment.assignmentStatus)}`}>
                      {formatCodeLabel(assignment.assignmentStatus, 'Assigned')}
                    </span>
                  </div>
                  {assignment.description ? (
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-text-muted">{assignment.description}</p>
                  ) : null}
                  {assignment.assignmentStatus === 'ASSIGNED' ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                        isLoading={updateAssignment.isPending}
                        onClick={() => updateAssignmentStatus(assignment, 'COMPLETED')}
                      >
                        Done
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        isLoading={updateAssignment.isPending}
                        onClick={() => updateAssignmentStatus(assignment, 'ARCHIVED')}
                      >
                        Archive
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-surface p-4">
              <p className="text-sm font-semibold text-text">No homework yet</p>
            </div>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-surface p-3">
              <div className="mb-3 flex items-center gap-2">
                <PlusCircle className="h-4 w-4 text-primary" />
                <p className="text-sm font-semibold text-text">Add drill</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Title"
                  value={drillTitle}
                  hint="Drill name."
                  onChange={(event) => setDrillTitle(event.target.value)}
                />
                <Input
                  label="Focus"
                  value={drillFocus}
                  hint="Skill area."
                  onChange={(event) => setDrillFocus(event.target.value)}
                />
                <Input
                  label="Minutes"
                  type="number"
                  min="1"
                  max="240"
                  value={drillDuration}
                  onChange={(event) => setDrillDuration(event.target.value)}
                />
              </div>
              {drillError ? <p className="mt-2 text-sm text-red-500">{drillError}</p> : null}
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="outline" isLoading={createDrill.isPending} onClick={submitDrill}>
                  Save drill
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface p-3">
              <div className="mb-3 flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-primary" />
                <p className="text-sm font-semibold text-text">Create plan</p>
              </div>
              <div className="space-y-3">
                <Input
                  label="Title"
                  value={planTitle}
                  hint="Plan name."
                  onChange={(event) => setPlanTitle(event.target.value)}
                />
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-text">First drill</label>
                  <select className={selectClassName} value={planDrillId} onChange={(event) => setPlanDrillId(event.target.value)}>
                    <option value="">No drill selected</option>
                    {drills.map((drill) => (
                      <option key={drill.drillId} value={drill.drillId}>
                        {drill.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {planError ? <p className="mt-2 text-sm text-red-500">{planError}</p> : null}
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="outline" isLoading={createLessonPlan.isPending} onClick={submitLessonPlan}>
                  Save plan
                </Button>
              </div>
            </div>
          </div>
          </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function MiniProgressMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-2 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-subtle">{label}</p>
      <p className="mt-1 text-base font-semibold tabular-nums text-text">{value}</p>
    </div>
  );
}

function ProgressStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border bg-surface px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums text-text">{value}</p>
    </div>
  );
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string;
  value: number;
  description: string;
}) {
  return (
    <Card variant="default">
      <CardContent className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
        <p className="text-2xl font-bold text-text">{value}</p>
        <p className="text-sm text-text-muted">{description}</p>
      </CardContent>
    </Card>
  );
}

function EmptyStateCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/30 p-5">
      <div className="flex items-center gap-2">
        {icon || <UserRound className="h-4 w-4 text-text-subtle" />}
        <p className="text-base font-semibold text-text">{title}</p>
      </div>
      <p className="mt-2 text-sm text-text-muted">{description}</p>
    </div>
  );
}

function DirectoryMemberPreview({
  member,
}: {
  member: {
    membershipId: number;
    displayName?: string | null;
    profileName?: string | null;
    membershipStatus?: string | null;
    email?: string | null;
    mobileNumber?: string | null;
    roleCode?: string | null;
    athleteNames?: string[] | null;
  };
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-text">
            {member.displayName || member.profileName || 'Tenant member'}
          </p>
          <p className="mt-1 text-sm text-text-muted">
            {[member.membershipStatus, member.email || member.mobileNumber].filter(Boolean).join(' • ')}
          </p>
        </div>
        <div className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${roleChipClasses(member.roleCode)}`}>
          {formatRole(member.roleCode)}
        </div>
      </div>

      {member.athleteNames?.length ? (
        <p className="mt-3 text-sm text-text-muted">
          Athletes: {member.athleteNames.join(', ')}
        </p>
      ) : null}
    </div>
  );
}
